import { useQuery, useMutation } from "@tanstack/react-query";
import React, { Component, useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";
import GoalCard from "@/components/goal-card";
import CreateGoalModal from "@/components/create-goal-modal";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Grid, List, Plus, Target, TrendingUp, CheckCircle, GripVertical, Users, Clock, ChevronDown, ChevronRight, ChevronsDown, ChevronsUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import type { Goal, Category } from "@shared/schema";
import { useSharedGoals } from "@/lib/queries/community";
import { useQuickCreateRoute } from "@/hooks/useQuickCreateRoute";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useGoalFilters } from "@/hooks/useGoalFilters";
import { isFutureGoal } from "@/lib/goal-visibility";
import { useToast } from "@/hooks/use-toast";

// ─── Per-card error boundary ────────────────────────────────────────────────
interface GoalCardEBProps { goal: any; children: React.ReactNode }
interface GoalCardEBState { hasError: boolean }

class GoalCardErrorBoundary extends Component<GoalCardEBProps, GoalCardEBState> {
  constructor(props: GoalCardEBProps) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): GoalCardEBState {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    fetch('/api/log-client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: `GoalCard crash [goal:${this.props.goal?.id}]: ${error.message}`,
        stack: error.stack,
        componentStack: info.componentStack,
        goalData: JSON.stringify(this.props.goal ?? {}),
      }),
    }).catch(() => {});
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-destructive/30 rounded-lg p-4 bg-destructive/5 text-center">
          <p className="text-sm text-destructive font-medium">Failed to display goal</p>
          <p className="text-xs text-muted-foreground mt-1">{this.props.goal?.title ?? 'Unknown goal'}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Goal Category Overview ────────────────────────────────────────────────

function CategoryPieChart({
  categories,
  goalsByCategory,
}: {
  categories: Category[];
  goalsByCategory: Record<string, Goal[]>;
}) {
  const cx = 90, cy = 90, r = 78, innerR = 34;
  const n = categories.length;
  if (n === 0) return null;

  const activeCount = categories.filter(c => (goalsByCategory[c.name] || []).length > 0).length;

  return (
    <svg viewBox="0 0 180 180" className="w-full max-w-[190px] mx-auto drop-shadow-sm">
      {categories.map((cat, i) => {
        const startAngle = (i / n) * 2 * Math.PI - Math.PI / 2;
        const endAngle = ((i + 1) / n) * 2 * Math.PI - Math.PI / 2;
        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        const ix1 = cx + innerR * Math.cos(startAngle);
        const iy1 = cy + innerR * Math.sin(startAngle);
        const ix2 = cx + innerR * Math.cos(endAngle);
        const iy2 = cy + innerR * Math.sin(endAngle);
        const hasGoals = (goalsByCategory[cat.name] || []).length > 0;

        const d = [
          `M ${ix1} ${iy1}`,
          `L ${x1} ${y1}`,
          `A ${r} ${r} 0 0 1 ${x2} ${y2}`,
          `L ${ix2} ${iy2}`,
          `A ${innerR} ${innerR} 0 0 0 ${ix1} ${iy1}`,
          "Z",
        ].join(" ");

        return (
          <path
            key={cat.id}
            d={d}
            fill={hasGoals ? cat.color : "#f3f4f6"}
            stroke="white"
            strokeWidth="2"
            opacity={hasGoals ? 1 : 0.9}
          />
        );
      })}
      <text x={cx} y={cy - 7} textAnchor="middle" fontSize="22" fontWeight="700" fill="#1f2937">
        {activeCount}
      </text>
      <text x={cx} y={cy + 11} textAnchor="middle" fontSize="9" fill="#6b7280">
        of {n} active
      </text>
    </svg>
  );
}

function GoalCategoryOverview({
  goals: allGoals,
  statusFilter,
  onStatusFilterChange,
}: {
  goals: Goal[];
  statusFilter: "active" | "achieved";
  onStatusFilterChange: (value: "active" | "achieved") => void;
}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [expanded, setExpanded] = useState<boolean>(true);

  // Only include goals matching the selected status in all charts/percentages.
  const goals = allGoals.filter(g => {
    const isAchieved = g.status === "completed" || g.progress === 100;
    return statusFilter === "achieved" ? isAchieved : !isAchieved;
  });

  // Load from user-scoped key once userId is known.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(userLsKey("goals_category_overview_expanded", userId));
      if (stored !== null) setExpanded(stored !== "false");
      if (userId) localStorage.removeItem("goals_category_overview_expanded");
    } catch {}
  }, [userId]);

  const { data: allCategories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const goalCategories = allCategories.filter(c => c.scope === "goals");

  const goalsByCategory = goalCategories.reduce<Record<string, Goal[]>>((acc, cat) => {
    if (!cat.name) return acc;
    acc[cat.name] = goals.filter(
      g => g.category?.toLowerCase() === cat.name?.toLowerCase()
    );
    return acc;
  }, {});

  const statsPerCategory = goalCategories.map(cat => {
    const catGoals = goalsByCategory[cat.name] || [];
    const avgProgress =
      catGoals.length > 0
        ? Math.round(catGoals.reduce((sum, g) => sum + g.progress, 0) / catGoals.length)
        : 0;
    return { ...cat, goals: catGoals, avgProgress };
  });

  const maxProgress = Math.max(...statsPerCategory.map(c => c.avgProgress), 1);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try {
      localStorage.setItem(userLsKey("goals_category_overview_expanded", userId), String(next));
    } catch {}
  };

  return (
    <Card className="mb-4 md:mb-6 overflow-hidden">
      {/* Header row — always visible. Collapse and the status toggle are
          sibling native buttons (no nested interactive controls). */}
      <div className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-muted/50 transition-colors">
        <button
          type="button"
          onClick={toggle}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          )}
          <span className="font-semibold text-sm">Goal Category Overview</span>
          <span className="text-xs text-muted-foreground ml-1">
            ({statsPerCategory.filter(c => c.goals.length > 0).length} of {goalCategories.length} categories active)
          </span>
        </button>
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Active/Achieved toggle */}
          <div
            className="flex items-center rounded-md border border-border overflow-hidden"
            role="group"
            aria-label="Show active or achieved goals"
          >
            <button
              type="button"
              onClick={() => onStatusFilterChange("active")}
              aria-pressed={statusFilter === "active"}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                statusFilter === "active"
                  ? "bg-primary text-primary-foreground"
                  : "bg-transparent text-muted-foreground hover:bg-muted"
              }`}
              data-testid="button-overview-active"
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => onStatusFilterChange("achieved")}
              aria-pressed={statusFilter === "achieved"}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                statusFilter === "achieved"
                  ? "bg-primary text-primary-foreground"
                  : "bg-transparent text-muted-foreground hover:bg-muted"
              }`}
              data-testid="button-overview-achieved"
            >
              Achieved
            </button>
          </div>
          <button
            type="button"
            onClick={toggle}
            className="text-xs text-muted-foreground"
            aria-expanded={expanded}
          >
            {expanded ? "Collapse" : "Expand"}
          </button>
        </div>
      </div>

      {expanded && (
        <CardContent className="px-4 pb-4 pt-2">
          {goalCategories.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Loading categories…
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              {/* ── Column 1: Pie chart ── */}
              <div className="flex flex-col items-center gap-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Distribution</p>
                <CategoryPieChart categories={goalCategories} goalsByCategory={goalsByCategory} />
                {/* Legend */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 w-full">
                  {goalCategories.map(cat => {
                    const hasGoals = (goalsByCategory[cat.name] || []).length > 0;
                    return (
                      <div key={cat.id} className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-white"
                          style={{ backgroundColor: hasGoals ? cat.color : "#d1d5db" }}
                        />
                        <span
                          className="text-xs truncate"
                          style={{ color: hasGoals ? cat.color : "#9ca3af" }}
                        >
                          {cat.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Column 2: Progress per category ── */}
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Progress</p>
                {statsPerCategory.map(cat => (
                  <div key={cat.id} className="space-y-0.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.goals.length > 0 ? cat.color : "#d1d5db" }}
                        />
                        <span
                          className="text-xs font-medium"
                          style={{ color: cat.goals.length > 0 ? "inherit" : "#9ca3af" }}
                        >
                          {cat.name}
                        </span>
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {cat.goals.length > 0 ? `${cat.avgProgress}%` : "—"}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${cat.avgProgress}%`,
                          backgroundColor: cat.goals.length > 0 ? cat.color : "transparent",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Column 3: Bar chart ── */}
              <div className="flex flex-col gap-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Comparison</p>

                {/* Bar area — fixed height so all bars share the same zero baseline */}
                <div className="flex items-end gap-1.5 w-full" style={{ height: "120px" }}>
                  {statsPerCategory.map(cat => {
                    const barHeight = maxProgress > 0 ? (cat.avgProgress / 100) * 100 : 0;
                    const hasGoals = cat.goals.length > 0;
                    return (
                      <div key={cat.id} className="flex flex-col items-center justify-end flex-1 h-full">
                        <span className="text-[10px] tabular-nums text-muted-foreground leading-none mb-0.5">
                          {hasGoals ? `${cat.avgProgress}%` : ""}
                        </span>
                        <div
                          className="w-full rounded-t-sm transition-all duration-500 relative group"
                          style={{
                            height: hasGoals ? `${Math.max(barHeight, 2)}%` : "2%",
                            backgroundColor: hasGoals ? cat.color : "#e5e7eb",
                            minHeight: "3px",
                          }}
                        >
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:flex bg-popover text-popover-foreground text-[10px] rounded px-1.5 py-0.5 shadow whitespace-nowrap z-10 border">
                            {cat.name}: {hasGoals ? `${cat.avgProgress}% avg` : "no goals"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Baseline */}
                <div className="border-t border-border w-full" />

                {/* Label area — separate row so baseline is always consistent */}
                <div className="flex gap-1.5 w-full mt-1">
                  {statsPerCategory.map(cat => {
                    const hasGoals = cat.goals.length > 0;
                    return (
                      <div key={cat.id} className="flex-1 flex justify-center" style={{ height: "64px" }}>
                        <span
                          className="text-[9px] leading-none"
                          style={{
                            color: hasGoals ? cat.color : "#9ca3af",
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                          title={cat.name}
                        >
                          {cat.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ─── Main Goals Page ────────────────────────────────────────────────────────

export default function GoalsPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  useQuickCreateRoute("goal", () => setIsCreateModalOpen(true));
  const [expandAllSignal, setExpandAllSignal] = useState<{ expanded: boolean; seq: number } | null>(null);
  const [activeTab, setActiveTab] = useState("my-goals");
  
  const { filters, updateFilters } = useGoalFilters({
    goalTypeFilter: "current",
    categoryFilter: "all",
    achievementFilter: "active",
    viewMode: "list",
  });
  
  const goalTypeFilter = (typeof filters.goalTypeFilter === "string" ? filters.goalTypeFilter : null) || "current";
  const selectedCategory = (typeof filters.categoryFilter === "string" ? filters.categoryFilter : null) || "all";
  const achievementFilter = (typeof filters.achievementFilter === "string" ? filters.achievementFilter : null) || "active";
  const viewMode = (typeof filters.viewMode === "string" ? filters.viewMode : null) || "list";
  const overviewStatusFilter = filters.overviewStatusFilter === "achieved" ? "achieved" : "active";

  const { data: allFetchedGoals = [], isLoading } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });
  // Goals flagged to stay off the main page (e.g. fitness goals whose
  // "Show on main Goals page" toggle is off) only appear on their source page.
  const goals = allFetchedGoals.filter(goal => goal.showOnMainGoalPage !== false);

  const { data: allCategories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const { data: sharedGoals = [], isLoading: sharedLoading } = useSharedGoals();

  const filteredGoals = goals.filter(goal => {
    const categoryMatch = selectedCategory === "all" ||
      (goal.category ?? "").toLowerCase() === selectedCategory.toLowerCase();
    
    const isAchieved = goal.status === "completed" || goal.progress === 100;
    const achievementMatch = achievementFilter === "all" || 
      (achievementFilter === "achieved" ? isAchieved : !isAchieved);
    
    const goalTypeMatch = goalTypeFilter === "all" || 
      (goalTypeFilter === "future" ? isFutureGoal(goal) : !isFutureGoal(goal));
    
    return categoryMatch && goalTypeMatch && achievementMatch;
  });

  const goalStats = {
    total: goals.length,
    active: goals.filter(g => g.status === "active" && g.progress < 100).length,
    completed: goals.filter(g => g.status === "completed" || g.progress === 100).length,
    paused: goals.filter(g => g.status === "paused").length,
    averageProgress: goals.length > 0 
      ? Math.round(goals.reduce((sum, g) => sum + g.progress, 0) / goals.length)
      : 0
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading your goals...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4 md:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
              <Target className="h-5 w-5 md:h-6 md:w-6 flex-shrink-0" />
              <span className="truncate">My Goals</span>
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-1">
              Track your progress and achieve your dreams
            </p>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground w-full sm:w-auto flex-shrink-0"
            data-testid="button-create-goal"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="whitespace-nowrap">New Goal</span>
          </Button>
        </div>
      </div>

      <div className="flex-1 px-4 md:px-6 lg:px-8 py-4 md:py-6">

        {/* Category Overview — collapsible, persistent */}
        <GoalCategoryOverview
          goals={goals}
          statusFilter={overviewStatusFilter}
          onStatusFilterChange={(value) => updateFilters({ overviewStatusFilter: value })}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-4 md:mb-6">
          <Card>
            <CardContent className="p-3 md:p-4 text-center">
              <div className="text-xl md:text-2xl font-bold text-primary">{goalStats.total}</div>
              <div className="text-xs md:text-sm text-muted-foreground">Total Goals</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 text-center">
              <div className="text-xl md:text-2xl font-bold text-blue-600">{goalStats.active}</div>
              <div className="text-xs md:text-sm text-muted-foreground">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 text-center">
              <div className="text-xl md:text-2xl font-bold text-green-600">{goalStats.completed}</div>
              <div className="text-xs md:text-sm text-muted-foreground">Completed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4 text-center">
              <div className="text-xl md:text-2xl font-bold text-yellow-600">{goalStats.paused}</div>
              <div className="text-xs md:text-sm text-muted-foreground">Paused</div>
            </CardContent>
          </Card>
          <Card className="col-span-2 md:col-span-1">
            <CardContent className="p-3 md:p-4 text-center">
              <div className="text-xl md:text-2xl font-bold text-purple-600">{goalStats.averageProgress}%</div>
              <div className="text-xs md:text-sm text-muted-foreground">Avg Progress</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and View Controls */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between mb-4 md:mb-6 gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Select value={goalTypeFilter} onValueChange={(value: "current" | "future" | "all") => updateFilters({ goalTypeFilter: value })}>
              <SelectTrigger className="w-full sm:w-40" data-testid="select-goal-type-filter">
                <SelectValue placeholder="Current Goals" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current">
                  <span className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Current Goals
                  </span>
                </SelectItem>
                <SelectItem value="future">
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Future Goals
                  </span>
                </SelectItem>
                <SelectItem value="all">All Goals</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={(value) => updateFilters({ categoryFilter: value })}>
              <SelectTrigger className="w-full sm:w-48" data-testid="select-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {allCategories.filter(c => c.scope !== "goals").length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Custom</SelectLabel>
                    {allCategories.filter(c => c.scope !== "goals" && c.name).map(cat => (
                      <SelectItem key={cat.id} value={cat.name!}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {allCategories.filter(c => c.scope === "goals").length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Goal Categories</SelectLabel>
                    {allCategories.filter(c => c.scope === "goals" && c.name).map(cat => (
                      <SelectItem key={cat.id} value={cat.name!}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            <Select value={achievementFilter} onValueChange={(value: "all" | "active" | "achieved") => updateFilters({ achievementFilter: value })}>
              <SelectTrigger className="w-full sm:w-40" data-testid="select-achievement-filter">
                <SelectValue placeholder="Active" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Active
                  </span>
                </SelectItem>
                <SelectItem value="achieved">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Achieved
                  </span>
                </SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpandAllSignal({ expanded: true, seq: (expandAllSignal?.seq ?? 0) + 1 })}
              data-testid="button-expand-all-goals"
            >
              <ChevronsDown className="w-4 h-4 mr-1" />
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpandAllSignal({ expanded: false, seq: (expandAllSignal?.seq ?? 0) + 1 })}
              data-testid="button-collapse-all-goals"
            >
              <ChevronsUp className="w-4 h-4 mr-1" />
              Collapse All
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => updateFilters({ viewMode: "grid" })}
              className={viewMode === "grid" ? "bg-muted" : ""}
              data-testid="button-grid-view"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => updateFilters({ viewMode: "list" })}
              className={viewMode === "list" ? "bg-muted" : ""}
              data-testid="button-list-view"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Goals Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-auto mb-4">
            <TabsTrigger value="my-goals" className="flex items-center gap-1 sm:gap-2 text-sm" data-testid="tab-my-goals">
              <Target className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">My Goals</span>
            </TabsTrigger>
            <TabsTrigger value="shared" className="flex items-center gap-1 sm:gap-2 text-sm" data-testid="tab-shared-goals">
              <Users className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline truncate">Shared with Me</span>
              <span className="sm:hidden truncate">Shared</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-goals" className="mt-4">
            <GoalsGrid goals={filteredGoals} viewMode={viewMode} expandAllSignal={expandAllSignal} />
          </TabsContent>

          <TabsContent value="shared" className="mt-6">
            {sharedLoading ? (
              <div className="flex items-center justify-center min-h-[30vh]">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-muted-foreground">Loading shared goals...</p>
                </div>
              </div>
            ) : sharedGoals.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Shared Goals</h3>
                  <p className="text-muted-foreground mb-4">
                    Goals shared with you by friends will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground" data-testid="text-shared-goals-count">
                  {sharedGoals.length} goal{sharedGoals.length !== 1 ? 's' : ''} shared with you
                </p>
                <GoalsGrid goals={sharedGoals} viewMode={viewMode} isShared expandAllSignal={expandAllSignal} />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <CreateGoalModal 
        open={isCreateModalOpen} 
        onOpenChange={setIsCreateModalOpen} 
      />
    </>
  );
}

// ─── Sortable Goal Item ─────────────────────────────────────────────────────

type ExpandAllSignal = { expanded: boolean; seq: number } | null;

function SortableGoalItem({ goal, viewMode, expandAllSignal }: { goal: Goal; viewMode: "grid" | "list"; expandAllSignal?: ExpandAllSignal }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: goal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (viewMode === "grid") {
    return <GoalCard goal={goal} viewMode={viewMode} expandAllSignal={expandAllSignal} />;
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <button
        className="touch-none cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${goal.id}`}
      >
        <GripVertical className="w-5 h-5 text-muted-foreground" />
      </button>
      <div className="flex-1">
        <GoalCard goal={goal} viewMode={viewMode} expandAllSignal={expandAllSignal} />
      </div>
    </div>
  );
}

// ─── Goals Grid ─────────────────────────────────────────────────────────────

function GoalsGrid({ goals, viewMode, isShared = false, expandAllSignal }: { goals: Goal[] | any[], viewMode: "grid" | "list", isShared?: boolean, expandAllSignal?: ExpandAllSignal }) {
  const { toast } = useToast();
  const [localGoals, setLocalGoals] = useState(goals);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const reorderMutation = useMutation({
    mutationFn: async (reorderedGoals: { id: string; sortOrder: number }[]) => {
      return apiRequest('PATCH', '/api/goals/reorder', { goals: reorderedGoals });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals'] });
    },
    onError: () => {
      // Roll the optimistic local ordering back to the server's order.
      setLocalGoals(goals);
      queryClient.invalidateQueries({ queryKey: ['/api/goals'] });
      toast({ title: "Couldn't reorder goals", description: "Your new order was not saved. Please try again.", variant: "destructive" });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalGoals((items: any[]) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        const reorderedGoals = newOrder.map((goal, index) => ({
          id: goal.id,
          sortOrder: index,
        }));
        
        reorderMutation.mutate(reorderedGoals);
        
        return newOrder;
      });
    }
  };

  useEffect(() => {
    setLocalGoals(goals);
  }, [goals]);

  if (localGoals.length === 0) {
    return (
      <div className="col-span-full text-center py-12">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Target className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No goals found</h3>
        <p className="text-muted-foreground mb-4">
          Start your journey by creating your first goal!
        </p>
      </div>
    );
  }

  if (viewMode === "grid") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {localGoals.map((goal: any) => (
          <GoalCardErrorBoundary key={goal.id} goal={goal}>
            <GoalCard goal={goal} viewMode={viewMode} expandAllSignal={expandAllSignal} />
          </GoalCardErrorBoundary>
        ))}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={localGoals.map((g: any) => g.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-4">
          {localGoals.map((goal: any) => (
            <GoalCardErrorBoundary key={goal.id} goal={goal}>
              <SortableGoalItem goal={goal} viewMode={viewMode} expandAllSignal={expandAllSignal} />
            </GoalCardErrorBoundary>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
